import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import clipoidLogo from '../../assets/clipoid_logo.png';
import { CardSpotlight } from '../components/aceternity/CardSpotlight';
import { api } from '../api';
import { useTurnstile } from '../hooks/useTurnstile';
// import { useOAuthSSE } from '../hooks/useOAuthSSE';

// PKCE Helper functions
const generateRandomString = (length: number): string => {
  const array = new Uint32Array(length / 2);
  window.crypto.getRandomValues(array);
  return Array.from(array, dec => ('0' + dec.toString(16)).slice(-2)).join('');
};

const sha256 = async (plain: string): Promise<ArrayBuffer> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return window.crypto.subtle.digest('SHA-256', data);
};

const base64urlencode = (a: ArrayBuffer): string => {
  return btoa(String.fromCharCode.apply(null, new Uint8Array(a) as any))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const hashed = await sha256(verifier);
  return base64urlencode(hashed);
};


function LoginPage() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [identifierError, setIdentifierError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSocialLoginLoading, setIsSocialLoginLoading] = useState<null | 'google' | 'twitch' | 'kick'>(null);
  const [error, setError] = useState<React.ReactNode | null>(null);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const {
    turnstileToken, 
    turnstileError, 
    turnstileWidgetRef, 
    setTurnstileError,
    resetTurnstile 
  } = useTurnstile({ action: 'login' });

  const { login, isAuthenticated, registerOnLoginSuccess, unregisterOnLoginSuccess, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Generate a unique session ID for this login attempt
  const sessionId = React.useMemo(() => {
    return `login_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Track current popup for cleanup
  const currentPopupRef = React.useRef<Window | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const errorMessageFromUrl = params.get('error') || params.get('error_message');
    const provider = params.get('provider');

    if (errorMessageFromUrl) {
      if (provider === 'twitch' && errorMessageFromUrl.startsWith('This Twitch account was recently unlinked.')) {
        setError(
          <>
            <p>This Twitch account was recently unlinked. Would you like to try logging in with it again?</p>
            <button
              onClick={() => handleSocialLogin('twitch', true)}
              className="mt-2 w-full flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
              disabled={isSocialLoginLoading === 'twitch'}
            >
              {isSocialLoginLoading === 'twitch' ? 'Connecting...' : 'Relink Twitch Account'}
            </button>
          </>
        );
      } else {
        let displayMessage = decodeURIComponent(errorMessageFromUrl.replace(/\+/g, ' '));
        if (provider) {
          const formattedProvider = provider.charAt(0).toUpperCase() + provider.slice(1);
          displayMessage = `${formattedProvider} login failed: ${displayMessage}`;
        }
        if (errorMessageFromUrl.includes('Invalid or expired state')) {
            displayMessage += ' Please try logging in again.';
        }
        setError(displayMessage);
      }
    }
  }, [location.search, isSocialLoginLoading]);

  useEffect(() => {
    const handleLoginNavigate = () => {
      navigate('/');
    };

    if (isAuthenticated) {
        navigate('/');
        return undefined;
    }

    registerOnLoginSuccess(handleLoginNavigate);

    return () => {
      unregisterOnLoginSuccess(handleLoginNavigate);
    };
  }, [isAuthenticated, navigate, registerOnLoginSuccess, unregisterOnLoginSuccess]);

  const validateIdentifier = (identifierValue: string) => {
    if (!identifierValue) {
      return 'Email or Username is required.';
    } else if (identifierValue.length < 3) {
      return 'Email or Username must be at least 3 characters.';
    }
    return '';
  };

  const validatePassword = (password: string) => {
    if (!password) {
      return 'Password is required.';
    } else if (password.length < 6) {
      return 'Password must be at least 6 characters.';
    }
    return '';
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTurnstileError(null);

    const identifierValidationError = validateIdentifier(identifier);
    const passwordValidationError = validatePassword(password);

    setIdentifierError(identifierValidationError);
    setPasswordError(passwordValidationError);
    setError(null);

    if (!turnstileToken) {
      setTurnstileError('Please complete the CAPTCHA verification.');
      turnstileWidgetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (!identifierValidationError && !passwordValidationError) {
      setIsSubmitting(true);
      try {
        const isAdminLoginPath = window.location.pathname.startsWith('/admin/login') || window.location.pathname.startsWith('/admin');
        const result = await login(identifier, password, turnstileToken, isAdminLoginPath);
        if (!result.success) {
          setError(result.message || 'Login failed. Please check your credentials.');
          resetTurnstile();
        }
      } catch (err) {
        setError('An error occurred during login. Please try again later.');
        resetTurnstile();
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'twitch' | 'kick', isRelink: boolean = false) => {
    if (isSocialLoginLoading === provider) {
      return;
    }
    setIsSocialLoginLoading(provider);
    if (!isRelink || (isRelink && typeof error === 'string')) {
        setError(null);
    }
    setTurnstileError(null);

    if (!turnstileToken) {
      setTurnstileError('Please complete the CAPTCHA verification before proceeding.');
      if (turnstileWidgetRef.current) {
        turnstileWidgetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setIsSocialLoginLoading(null);
      return;
    }

    try {
      // Close any existing popup
      if (currentPopupRef.current && !currentPopupRef.current.closed) {
        currentPopupRef.current.close();
        currentPopupRef.current = null;
      }

      if (provider === 'kick') {
        const codeVerifier = generateRandomString(64);
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        sessionStorage.setItem('kick_code_verifier', codeVerifier);

        const response = await api.post('/auth/kick', {
          code_challenge: codeChallenge,
          cfTurnstileResponse: turnstileToken,
          session_id: sessionId, // Pass session_id to backend
        });

        if (response.data.success && response.data.url) {
          // Use popup for Kick as well
          const width = 500;
          const height = 600;
          const left = (window.screen.width / 2) - (width / 2);
          const top = (window.screen.height / 2) - (height / 2);
          
          // Prepare popup URL with session ID and popup indicator (Kick)
          let popupUrl = response.data.url;
          if (sessionId) {
            const url = new URL(response.data.url);
            url.searchParams.set('session_id', sessionId);
            url.searchParams.set('popup', 'true'); // Add popup indicator
            popupUrl = url.toString();
          }
          
          const popup = window.open(
            popupUrl,
            'kickAuth',
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,status=yes,location=no,toolbar=no,menubar=no`
          );
          
          // Listen for postMessage - Production Secure Implementation
          const handleMessage = (event: MessageEvent) => {
            console.log('[LoginPage Kick] Received message:', {
              origin: event.origin,
              type: event.data?.type,
              provider: event.data?.provider,
              sessionId: event.data?.sessionId,
              timestamp: event.data?.timestamp ? new Date(event.data.timestamp).toISOString() : 'No timestamp'
            });
            
            // Security: Skip non-OAuth messages
            if (event.data?.source === 'react-devtools-bridge') {
              return;
            }
            
            // Security: Strict origin validation
            if (event.origin !== window.location.origin) {
              console.warn('[LoginPage Kick] SECURITY: Rejected message from untrusted origin:', event.origin);
              return;
            }
            
            // Security: Validate message structure
            if (!event.data?.type || !event.data?.provider || !event.data?.timestamp) {
              console.warn('[LoginPage Kick] SECURITY: Invalid message structure');
              return;
            }
            
            // Security: Check message age (prevent replay attacks)
            if (event.data.timestamp && typeof event.data.timestamp === 'number') {
              const messageAge = Date.now() - event.data.timestamp;
              if (messageAge > 60000) { // 60 seconds max
                console.warn('[LoginPage Kick] SECURITY: Message too old, ignoring');
                return;
              }
            } else {
              console.warn('[LoginPage Kick] SECURITY: Invalid timestamp in message');
              return;
            }
            
            if (event.data.type === 'oauth-success' && event.data.provider === 'kick') {
              console.log('[LoginPage Kick] OAuth success - processing');
              
              // Force close popup immediately
              if (popup && !popup.closed) {
                try {
                  popup.postMessage({ type: 'force-close' }, window.location.origin);
                  popup.close();
                  console.log('[LoginPage Kick] Popup closed successfully');
                } catch (error) {
                  console.warn('[LoginPage Kick] Popup close failed:', error);
                }
              }
              
              cleanup();
              setIsSocialLoginLoading(null);
              
              // BUGFIX: Send confirmation to popup before navigation
              if (popup && !popup.closed) {
                try {
                  popup.postMessage({ type: 'auth-processed' }, window.location.origin);
                } catch (error) {
                  console.warn('[LoginPage] Failed to send auth-processed message:', error);
                }
              }
              
              navigate('/dashboard');
            } else if (event.data.type === 'oauth-error') {
              console.log('[LoginPage Kick] OAuth error:', event.data.error);
              
              if (popup && !popup.closed) {
                try {
                  popup.close();
                } catch (error) {
                  console.warn('[LoginPage Kick] Error popup close failed:', error);
                }
              }
              
              cleanup();
              setIsSocialLoginLoading(null);
              setError(event.data.error || 'Kick login failed');
            }
          };
          
          window.addEventListener('message', handleMessage);
          
          // No server polling needed - using postMessage only
          let pollInterval: NodeJS.Timeout | null = null;
          
          // Cleanup function
          const cleanup = () => {
            window.removeEventListener('message', handleMessage);
            if (pollInterval) clearInterval(pollInterval);
            if (checkClosed) clearInterval(checkClosed);
          };
          
          // Monitor popup closing and check auth status (Kick)
          let checkClosed: NodeJS.Timeout | null = null;
          
          const monitorKickPopupClose = () => {
            checkClosed = setInterval(async () => {
              try {
                if (popup && popup.closed) {
                  console.log('[LoginPage] 🔍 Kick popup closed detected, checking auth status...');
                  clearInterval(checkClosed!);
                  
                  // Wait for cookies to be available
                  setTimeout(async () => {
                    try {
                      // Check if user is now authenticated
                      await checkAuth();
                      console.log('[LoginPage] ✅ Kick auth check completed, redirecting...');
                      cleanup();
                      setIsSocialLoginLoading(null);
                      window.location.href = '/dashboard';
                    } catch (authError) {
                      console.error('[LoginPage] ❌ Kick auth check failed:', authError);
                      setIsSocialLoginLoading(null);
                      setError('Kick login failed. Please try again.');
                    }
                  }, 500); // 500ms delay for cookies
                }
              } catch (error) {
                console.warn('[LoginPage] Error checking Kick popup status:', error);
              }
            }, 250); // Check every 250ms
          };
          
          // Start monitoring Kick popup close
          monitorKickPopupClose();
        } else {
           throw new Error(response.data.message || 'Failed to get Kick auth URL.');
        }
      } else {
        // For OAuth providers, make request to get redirect URL
        const response = await api.get(`/auth/${provider}`, {
          params: {
            session_id: sessionId,
            popup: 'true',
            turnstile: turnstileToken || '',
            ...(provider === 'twitch' && isRelink && { relink: 'true' })
          }
        });

        if (response.data.success && response.data.url) {
          // Use popup for all social logins
          // Calculate center position
          const width = 500;
          const height = 600;
          const left = (window.screen.width / 2) - (width / 2);
          const top = (window.screen.height / 2) - (height / 2);
          
          // Open OAuth in centered popup window with actual OAuth URL
          const popup = window.open(
            response.data.url,
            `${provider}Auth`,
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,status=yes,location=no,toolbar=no,menubar=no`
          );

        if (popup) {
          
          // Listen for postMessage (primary method) - Production Secure Implementation
          const handleMessage = async (event: MessageEvent) => {
            console.log('[LoginPage] ===== RECEIVED MESSAGE =====');
            console.log('[LoginPage] Event source window === popup:', event.source === popup);
            console.log('[LoginPage] Event origin:', event.origin);
            console.log('[LoginPage] Current origin:', window.location.origin);
            console.log('[LoginPage] Full event data:', event.data);
            console.log('[LoginPage] Message details:', {
              origin: event.origin,
              type: event.data?.type,
              provider: event.data?.provider,
              sessionId: event.data?.sessionId,
              timestamp: event.data?.timestamp ? new Date(event.data.timestamp).toISOString() : 'No timestamp'
            });
            
            // Security: Skip non-OAuth messages
            if (event.data?.source === 'react-devtools-bridge' || 
                event.data?.source === 'react-devtools-content-script') {
              return;
            }
            
            // Security: Strict origin validation
            if (event.origin !== window.location.origin) {
              console.warn('[LoginPage] SECURITY: Rejected message from untrusted origin:', event.origin);
              return;
            }
            
            // Security: Validate message structure
            if (!event.data?.type || !event.data?.provider || !event.data?.timestamp) {
              console.warn('[LoginPage] SECURITY: Invalid message structure');
              return;
            }
            
            // Security: Check message age (prevent replay attacks)
            if (event.data.timestamp && typeof event.data.timestamp === 'number') {
              const messageAge = Date.now() - event.data.timestamp;
              if (messageAge > 60000) { // 60 seconds max
                console.warn('[LoginPage] SECURITY: Message too old, ignoring');
                return;
              }
            } else {
              console.warn('[LoginPage] SECURITY: Invalid timestamp in message');
              return;
            }
            
            if (event.data.type === 'oauth-success' && event.data.provider === provider) {
              console.log(`[LoginPage] ${provider} OAuth success - processing`);
              
              // Don't try to close popup due to COOP policy - popup closes itself
              console.log(`[LoginPage] ${provider} OAuth success - popup will close itself`);
              
              // Force reload to dashboard to get fresh cookies from popup
              console.log(`[LoginPage] ${provider} OAuth success - redirecting to dashboard`);
              cleanup();
              setIsSocialLoginLoading(null);
              
              // BUGFIX: Send confirmation to popup before navigation
              if (popup && !popup.closed) {
                try {
                  popup.postMessage({ type: 'auth-processed' }, window.location.origin);
                } catch (error) {
                  console.warn('[LoginPage] Failed to send auth-processed message:', error);
                }
              }
              
              window.location.href = '/dashboard';
            } else if (event.data.type === 'oauth-error') {
              console.log(`[LoginPage] ${provider} OAuth error:`, event.data.error);
              
              if (popup && !popup.closed) {
                try {
                  popup.close();
                } catch (error) {
                  console.warn(`[LoginPage] Error popup close failed:`, error);
                }
              }
              
              cleanup();
              setIsSocialLoginLoading(null);
              setError(event.data.error || `${provider} login failed`);
            }
          };
          
          window.addEventListener('message', handleMessage);
          
          // No server polling needed - using postMessage only
          let pollInterval: NodeJS.Timeout | null = null;
          
          // Cleanup function
          const cleanup = () => {
            window.removeEventListener('message', handleMessage);
            if (pollInterval) clearInterval(pollInterval);
            if (checkClosed) clearInterval(checkClosed);
          };
          
          // Monitor popup closing and check auth status (All providers)
          let checkClosed: NodeJS.Timeout | null = null;
          
          const monitorPopupClose = () => {
            checkClosed = setInterval(async () => {
              try {
                if (popup && popup.closed) {
                  console.log(`[LoginPage] 🔍 ${provider} popup closed detected, checking auth status...`);
                  clearInterval(checkClosed!);
                  
                  // Wait for cookies to be available
                  setTimeout(async () => {
                    try {
                      // Check if user is now authenticated
                      await checkAuth();
                      console.log(`[LoginPage] ✅ ${provider} auth check completed, redirecting...`);
                      cleanup();
                      setIsSocialLoginLoading(null);
                      window.location.href = '/dashboard';
                    } catch (authError) {
                      console.error(`[LoginPage] ❌ ${provider} auth check failed:`, authError);
                      setIsSocialLoginLoading(null);
                      setError(`${provider} login failed. Please try again.`);
                    }
                  }, 500); // 500ms delay for cookies
                }
              } catch (error) {
                console.warn(`[LoginPage] Error checking ${provider} popup status:`, error);
              }
            }, 250); // Check every 250ms
          };
          
          // Start monitoring popup close for all providers
          monitorPopupClose();
        } else {
          throw new Error(`Popup blocked or could not open ${provider} authentication window.`);
        }
        } else {
          throw new Error(response.data.message || `Failed to get ${provider} auth URL.`);
        }
      }
    } catch (err: any) {
      let errorMessage = err.response?.data?.message || err.message || `An error occurred while trying to connect with ${provider}.`;
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors) && err.response.data.errors.length > 0) {
          errorMessage += ` (Details: ${err.response.data.errors.join(', ')})`;
      }
      if (errorMessage.toLowerCase().includes('captcha')) {
          setTurnstileError(errorMessage);
      }
      setError(errorMessage);
      resetTurnstile();
      setIsSocialLoginLoading(null);
    }
  };

  return (
    <div className="min-h-screen h-screen w-full flex items-center justify-center bg-black antialiased relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/10 to-black"></div>
      
      {/* Floating orbs for depth */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      
      <CardSpotlight className="group/spotlight w-full max-w-sm glass-card">
        <div className="relative z-20">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <img src={clipoidLogo} alt="Clipoid Logo" className="h-12 relative z-10" />
              <div className="absolute inset-0 bg-white/20 rounded-full blur-xl scale-150"></div>
            </div>
          </div>
          <h1 className="text-2xl font-bold mb-4 text-center bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
            Login
          </h1>
          <div className="glass-card bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 px-3 py-2 rounded-lg w-full mb-4 text-xs backdrop-blur-sm flex items-center space-x-2" role="alert">
            <svg className="w-4 h-4 text-yellow-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-semibold">Closed Beta</p>
              <p className="text-xs opacity-90">Restricted to authorized test users only.</p>
            </div>
          </div>
          {error && (
            <div className="glass-card bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-3 rounded-xl w-full mb-4 text-sm backdrop-blur-sm" role="alert">
              {error}
            </div>
          )}
          
          {/* Social Login Section */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-center text-foreground mb-4">Continue with</h2>
            
            {/* Social Icons Grid */}
            <div className="flex justify-center space-x-4">
              <button
                type="button"
                onClick={() => handleSocialLogin('google')}
                className="glass-button p-3 border border-white/10 rounded-xl hover:bg-white/[0.05] hover:border-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background focus:ring-primary/50 transition duration-300 ease-in-out disabled:opacity-50 backdrop-blur-md transform hover:-translate-y-0.5"
                disabled={isSubmitting || !!isSocialLoginLoading}
                title="Continue with Google"
              >
                {isSocialLoginLoading === 'google' ? (
                  <svg className="animate-spin h-6 w-6 text-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <img src="/assets/googleicon.svg" alt="Google" className="h-6 w-6" />
                )}
              </button>
              
              <button
                type="button"
                onClick={() => {}} // Disabled
                className="glass-button p-3 border border-white/10 rounded-xl transition duration-300 ease-in-out opacity-30 cursor-not-allowed"
                disabled={true}
                title="Twitch login temporarily disabled"
              >
                <img src="/assets/twitchicon.svg" alt="Twitch" className="h-6 w-6 grayscale" />
              </button>
              
              <button
                type="button"
                onClick={() => {}} // Disabled
                className="glass-button p-3 border border-white/10 rounded-xl transition duration-300 ease-in-out opacity-30 cursor-not-allowed"
                disabled={true}
                title="Kick login temporarily disabled"
              >
                <img src="/assets/kickicon.svg" alt="Kick" className="h-6 w-6 grayscale" />
              </button>
            </div>
            
            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-white/10" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 glass-card rounded-full text-muted-foreground border border-white/10">or</span>
              </div>
            </div>
            
            {/* CAPTCHA for Social Login */}
            <div ref={turnstileWidgetRef} className="cf-turnstile-container my-4 h-[65px]"></div>
            {turnstileError && <p className="text-red-400 text-xs mt-1 mb-3 text-center">{turnstileError}</p>}
            
            {/* Email Login Toggle */}
            <button
              type="button"
              onClick={() => setShowEmailLogin(!showEmailLogin)}
              className="w-full glass-button bg-white/[0.02] border border-white/10 rounded-xl py-2.5 px-4 text-foreground hover:bg-white/[0.05] hover:border-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background focus:ring-primary/50 transition duration-300 ease-in-out backdrop-blur-md flex items-center justify-center space-x-2"
            >
              <span>Continue with Email</span>
              <svg className={`w-4 h-4 transition-transform duration-200 ${showEmailLogin ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          
          {/* Expandable Email Login Form */}
          {showEmailLogin && (
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label htmlFor="identifier" className="sr-only">Email or Username</label>
                <input
                  type="text"
                  id="identifier"
                  placeholder="Email or Username"
                  className={`block w-full px-3 py-2.5 glass-card bg-white/[0.02] border ${identifierError ? 'border-red-500/50' : 'border-white/10'} rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/30 focus:bg-white/[0.05] transition duration-300 ease-in-out backdrop-blur-md`}
                  value={identifier}
                  onChange={(e) => { setIdentifier(e.target.value); setIdentifierError(''); if (typeof error === 'string' || error === null) setError(null); }}
                  disabled={isSubmitting}
                  aria-invalid={!!identifierError}
                  aria-describedby={identifierError ? "identifier-error" : undefined}
                />
                {identifierError && <p id="identifier-error" className="text-red-400 text-xs mt-1">{identifierError}</p>}
              </div>
              <div>
                <label htmlFor="password" className="sr-only">Password</label>
                <input
                  type="password"
                  id="password"
                  placeholder="Password"
                  className={`block w-full px-3 py-2.5 glass-card bg-white/[0.02] border ${passwordError ? 'border-red-500/50' : 'border-white/10'} rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/30 focus:bg-white/[0.05] transition duration-300 ease-in-out backdrop-blur-md`}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setPasswordError(''); if (typeof error === 'string' || error === null) setError(null); }}
                  disabled={isSubmitting}
                  aria-invalid={!!passwordError}
                  aria-describedby={passwordError ? "password-error" : undefined}
                />
                {passwordError && <p id="password-error" className="text-red-400 text-xs mt-1">{passwordError}</p>}
              </div>
              <button
                type="submit"
                className="w-full glass-button bg-gradient-to-r from-primary/80 to-blue-600/80 text-primary-foreground hover:from-primary hover:to-blue-600 font-semibold py-2.5 px-4 rounded-xl shadow-2xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background focus:ring-primary/50 disabled:opacity-60 disabled:cursor-not-allowed transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-2xl flex items-center justify-center backdrop-blur-md border border-white/10 relative overflow-hidden group"
                disabled={isSubmitting}
              >
                {/* Shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700 ease-out"></div>
                
                <span className="relative z-10">
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Logging in...
                    </>
                  ) : (
                    'Login'
                  )}
                </span>
              </button>
            </form>
          )}
          
          {/* Footer Links */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-6 text-sm space-y-2 sm:space-y-0">
            <RouterLink
              to="/forgot-password"
              className="px-3 py-1.5 rounded-xl text-sm text-muted-foreground glass-button hover:bg-white/[0.05] hover:text-foreground hover:border-white/20 transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background focus:ring-primary/50 border border-white/10 backdrop-blur-md"
            >
              Forgot your password?
            </RouterLink>
            <RouterLink
              to="/register"
              className="px-3 py-1.5 rounded-xl text-sm text-primary glass-button hover:bg-primary/10 hover:text-primary/80 hover:border-primary/30 transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background focus:ring-primary/50 border border-primary/20 backdrop-blur-md"
            >
              Don't have an account? <span className="font-semibold">Sign Up</span>
            </RouterLink>
          </div>
        </div>
      </CardSpotlight>
    </div>
  );
}

export default LoginPage;